import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Seo } from "../components/Seo";

function StatusBadge({ v }) {
  const responded = v.has_response;
  return (
    <span data-testid={`status-${v.video_id}`}
          className={`text-[10px] uppercase tracking-widest px-2 py-1 ${responded ? "bg-[#C9A227] text-[#050A08]" : "bg-white/10 text-white/70"}`}>
      {responded ? "Responded" : "Pending"}
    </span>
  );
}

const respTime = (v) => new Date(v.coach_response?.created_at || v.created_at).getTime();
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

export default function MySwings() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/videos/mine");
      setVideos(data);
    } catch (e) {
      console.error("Failed to load my swings:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user === null) { nav("/login"); return; }
    if (user) load();
  }, [user, nav, load]);

  const togglePublish = async (e, v) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/videos/${v.video_id}/publish`, { published: !v.is_published });
      setVideos((list) => list.map((x) => (x.video_id === v.video_id ? { ...x, is_published: data.is_published } : x)));
    } catch (err) {
      console.error("Publish toggle failed:", err);
    }
  };

  if (!user) return null;

  return (
    <div data-testid="my-swings-page" className="min-h-screen pt-32 pb-24 px-6">
      <Seo title="My Swings" description="Your private swing uploads and coach responses." path="/my-swings" noindex />
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <div className="overline">Private locker</div>
            <h1 className="font-display text-5xl sm:text-6xl mt-3">My Swings</h1>
            <p className="text-white/60 mt-2">Your uploads go straight to your coach. Publish any to the public feed when you're ready.</p>
          </div>
          <Link to="/upload" className="btn-primary" data-testid="my-swings-upload">Upload Swing</Link>
        </div>

        {loading ? (
          <div className="text-white/50">Loading...</div>
        ) : videos.length === 0 ? (
          <div className="p-16 border border-white/10 bg-white/[0.02] text-center" data-testid="my-swings-empty">
            <h3 className="font-display text-3xl">No swings yet.</h3>
            <p className="text-white/60 mt-3">Upload your first swing and your coach will break it down.</p>
            <Link to="/upload" className="btn-primary mt-8 inline-block">Upload Swing</Link>
          </div>
        ) : (
          <>
            {(() => {
              const responses = videos.filter((v) => v.has_response).sort((a, b) => respTime(b) - respTime(a));
              if (responses.length === 0) return null;
              return (
                <section data-testid="my-responses-section" className="mb-16">
                  <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                    <div>
                      <div className="overline" style={{ color: "#C9A227" }}>Latest first</div>
                      <h2 className="font-display text-3xl mt-1">Video Responses</h2>
                    </div>
                    <span className="text-white/40 text-sm">{responses.length} response{responses.length === 1 ? "" : "s"}</span>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {responses.map((v, i) => (
                      <Link key={v.video_id} to={`/video/${v.video_id}`} data-testid={`my-response-${v.video_id}`}
                            className="border border-[#C9A227]/30 bg-[#C9A227]/[0.04] overflow-hidden block group hover:border-[#C9A227]/60 transition">
                        <div className="relative aspect-video bg-black">
                          <video src={`${API}/videos/${v.video_id}/file`} muted playsInline preload="metadata"
                                 className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition" />
                          <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <span className="w-11 h-11 rounded-full bg-black/50 flex items-center justify-center text-white text-lg">▶</span>
                          </span>
                          {i === 0 && (
                            <span data-testid={`response-latest-${v.video_id}`}
                                  className="absolute top-2 left-2 text-[10px] uppercase tracking-widest px-2 py-1 bg-[#C9A227] text-[#050A08] font-bold">Latest</span>
                          )}
                        </div>
                        <div className="p-4">
                          <h3 className="font-display text-xl leading-tight truncate">{v.title}</h3>
                          <div className="flex items-center justify-between mt-3">
                            <span className="text-xs text-[#C9A227]" data-testid={`watch-response-${v.video_id}`}>Watch response →</span>
                            <span className="text-white/40 text-xs">{fmtDate(v.coach_response?.created_at || v.created_at)}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              );
            })()}

            <section data-testid="my-uploads-section">
              <h2 className="font-display text-3xl mb-6">My Uploads</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {videos.map((v) => (
                  <div key={v.video_id} data-testid={`my-swing-${v.video_id}`} className="border border-white/10 bg-white/[0.02] overflow-hidden">
                    <Link to={`/video/${v.video_id}`} className="block relative aspect-video bg-black group">
                      <video src={`${API}/videos/${v.video_id}/file`} muted playsInline preload="metadata"
                             className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition" />
                      <div className="absolute top-2 left-2"><StatusBadge v={v} /></div>
                    </Link>
                    <div className="p-4">
                      <h3 className="font-display text-xl leading-tight">{v.title}</h3>
                      <div className="flex items-center justify-between mt-4">
                        <Link to={`/video/${v.video_id}`} className="text-xs text-[#C9A227] hover:underline" data-testid={`view-${v.video_id}`}>
                          {v.has_response ? "View response" : "View"}
                        </Link>
                        <button onClick={(e) => togglePublish(e, v)} data-testid={`publish-toggle-${v.video_id}`}
                                className={`text-[10px] uppercase tracking-widest px-3 py-1.5 border transition ${v.is_published ? "border-[#C9A227] text-[#C9A227]" : "border-white/20 text-white/60 hover:border-white/40"}`}>
                          {v.is_published ? "On Public Feed" : "Publish to Feed"}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
